import { useRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Dialog,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PanToolAltRoundedIcon from '@mui/icons-material/PanToolAltRounded';
import CropFreeRoundedIcon from '@mui/icons-material/CropFreeRounded';
import OpenWithRoundedIcon from '@mui/icons-material/OpenWithRounded';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import CommentRoundedIcon from '@mui/icons-material/CommentRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import DataObjectRoundedIcon from '@mui/icons-material/DataObjectRounded';

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

const MetaPanel = styled(Box)(({ theme }) => ({
  width: 290,
  padding: theme.spacing(3),
  borderRight: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const ImageTile = styled(Box)(({ theme }) => ({
  width: 170,
  borderRadius: 10,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const ToolRail = styled(Box)(({ theme }) => ({
  width: 54,
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(2, 1),
}));

const LeftEditorRail = styled(Box)(({ theme }) => ({
  width: 248,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
}));

const ModeRail = styled(Box)(({ theme }) => ({
  width: 58,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: theme.spacing(2),
  gap: theme.spacing(1),
}));

export default function AnnotateView({ isEditorOpen, onOpenEditor, onCloseEditor }) {
  const [zoom, setZoom] = useState(230);
  const [activeTool, setActiveTool] = useState('box');
  const [activeMode, setActiveMode] = useState('labels');
  const [annotationRect, setAnnotationRect] = useState({ x: 54, y: 19, width: 62, height: 154 });
  const dragStateRef = useRef(null);

  const openEditorFromTab = (_event, _value) => {
    onOpenEditor();
  };

  const updateZoom = (delta) => {
    setZoom((prev) => Math.min(400, Math.max(50, prev + delta)));
  };

  const startRectInteraction = (event, mode) => {
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      rect: annotationRect,
    };
    window.addEventListener('mousemove', handleRectInteraction);
    window.addEventListener('mouseup', stopRectInteraction);
  };

  const handleRectInteraction = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;

    const deltaX = ((event.clientX - dragState.startX) / window.innerWidth) * 100;
    const deltaY = ((event.clientY - dragState.startY) / window.innerHeight) * 100;

    setAnnotationRect((prev) => {
      const base = dragState.rect;
      if (dragState.mode === 'move') {
        return {
          ...prev,
          x: Math.max(5, Math.min(88, base.x + deltaX)),
          y: Math.max(5, Math.min(84, base.y + deltaY)),
        };
      }

      return {
        ...prev,
        width: Math.max(24, Math.min(140, base.width + deltaX)),
        height: Math.max(50, Math.min(240, base.height + deltaY)),
      };
    });
  };

  const stopRectInteraction = () => {
    dragStateRef.current = null;
    window.removeEventListener('mousemove', handleRectInteraction);
    window.removeEventListener('mouseup', stopRectInteraction);
  };

  return {
    main: (
      <>
        <MainCard>
          <Stack direction={{ xs: 'column', lg: 'row' }} alignItems={{ xs: 'flex-start', lg: 'center' }} justifyContent="space-between" spacing={2} sx={{ px: 3, py: 2.25 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: 24, md: 28 } }}>
              Uploaded on 04/21/26 at 3:09 pm: Job 2
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
                Submit for Review
              </Button>
              <Button variant="contained" startIcon={<AddPhotoAlternateRoundedIcon />} sx={{ textTransform: 'none', borderRadius: 2, px: 2 }}>
                Add 1 Image to Dataset
              </Button>
              <IconButton>
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch">
            <MetaPanel>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Progress
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={100}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      '& .MuiLinearProgress-bar': { borderRadius: 999 },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2 }}>
                    1 Images
                  </Typography>
                  <Stack spacing={0.6} sx={{ mt: 0.6 }}>
                    <Typography variant="caption" color="text.secondary">
                      1 Annotated
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      0 Unannotated
                    </Typography>
                  </Stack>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Instructions
                    </Typography>
                    <Button size="small" startIcon={<EditRoundedIcon />} sx={{ textTransform: 'none' }}>
                      Edit
                    </Button>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    No specific instructions were added when this job was assigned.
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Assignment
                    </Typography>
                    <Button size="small" startIcon={<AssignmentIndRoundedIcon />} sx={{ textTransform: 'none' }}>
                      Reassign
                    </Button>
                  </Stack>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Naveen Workspace-Precistat
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Labeler
                  </Typography>
                </Box>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
                    <TimelineRoundedIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Timeline
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: 'success.main' }}>N</Avatar>
                    <Box>
                      <Typography variant="caption" sx={{ lineHeight: 1.5, display: 'block', color: 'text.secondary' }}>
                        created this job by partially accepting another job. 1 image was added to the dataset and the remaining 1 image was added to this job and sent back for annotation.
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                        21/04/2026, 15:12:31
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </MetaPanel>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ px: 3, py: 1.5 }}>
                <Tabs value={1} sx={{ minHeight: 38 }} onChange={openEditorFromTab}>
                  <Tab label="Unannotated 0" disableRipple sx={{ textTransform: 'none', minHeight: 38 }} />
                  <Tab label="Annotated 1" disableRipple sx={{ textTransform: 'none', minHeight: 38 }} />
                </Tabs>

                <Select size="small" defaultValue="newest" sx={{ minWidth: 150, borderRadius: 2 }}>
                  <MenuItem value="newest">Sort By Newest</MenuItem>
                  <MenuItem value="oldest">Sort By Oldest</MenuItem>
                </Select>
              </Stack>

              <Divider />

              <Box sx={{ p: 3, minHeight: 520 }}>
                <ImageTile onClick={onOpenEditor} sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}>
                  <Box component="img" src="/static/demo4.png" alt="annotated" sx={{ display: 'block', width: '100%', height: 96, objectFit: 'cover' }} />
                  <Stack direction="row" alignItems="center" spacing={0.7} sx={{ px: 1, py: 0.9 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" color="text.secondary" noWrap>
                      10396_person_20260416_152915...
                    </Typography>
                  </Stack>
                </ImageTile>
              </Box>
            </Box>
          </Stack>
        </MainCard>

        <Dialog open={isEditorOpen} onClose={onCloseEditor} fullScreen>
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <IconButton size="small" onClick={onCloseEditor}>
                  <ArrowBackRoundedIcon fontSize="small" />
                </IconButton>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    MOVABLE OBJECTS &gt; ANNOTATE
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    10396_person_20260416_152915_3dce511d.jpg
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.25, py: 0.4, border: '1px solid', borderColor: 'divider', borderRadius: 999 }}>
                  <IconButton size="small">
                    <ChevronLeftRoundedIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center', fontWeight: 600 }}>
                    1/1
                  </Typography>
                  <IconButton size="small">
                    <ChevronRightRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 14 }}>✓</Avatar>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton size="small">
                  <VisibilityOffRoundedIcon fontSize="small" />
                </IconButton>
                <Button variant="contained" endIcon={<KeyboardArrowDownRoundedIcon />} sx={{ borderRadius: 999, textTransform: 'none', minWidth: 84 }}>
                  Train
                </Button>
                <IconButton size="small">
                  <MoreHorizRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
              <ModeRail>
                {[
                  { key: 'labels', label: 'Labels', icon: <LabelRoundedIcon fontSize="small" /> },
                  { key: 'attributes', label: 'Attributes', icon: <NotesRoundedIcon fontSize="small" /> },
                  { key: 'comments', label: 'Comments', icon: <CommentRoundedIcon fontSize="small" /> },
                  { key: 'history', label: 'History', icon: <HistoryRoundedIcon fontSize="small" /> },
                  { key: 'raw', label: 'Raw Data', icon: <DataObjectRoundedIcon fontSize="small" /> },
                ].map((item) => (
                  <Button
                    key={item.key}
                    onClick={() => setActiveMode(item.key)}
                    sx={{
                      minWidth: 0,
                      width: 42,
                      flexDirection: 'column',
                      gap: 0.5,
                      color: activeMode === item.key ? 'primary.main' : 'text.secondary',
                      textTransform: 'none',
                      fontSize: 10,
                      fontWeight: activeMode === item.key ? 700 : 500,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: activeMode === item.key ? 'primary.main' : 'divider',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: activeMode === item.key ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      }}
                    >
                      {item.icon}
                    </Box>
                    {item.label}
                  </Button>
                ))}
              </ModeRail>

              <LeftEditorRail>
                <Stack direction="row" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Button sx={{ minWidth: 0, flex: 1, py: 1.5, borderRadius: 0, color: 'primary.main' }}>Classes</Button>
                  <Button sx={{ minWidth: 0, flex: 1, py: 1.5, borderRadius: 0, color: 'text.secondary' }}>Layers</Button>
                </Stack>

                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>
                    Annotations <Typography component="span" variant="caption" color="text.secondary">1</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Group: movable-objects
                  </Typography>
                  <List dense disablePadding>
                    <ListItemButton sx={{ borderRadius: 2, mb: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 1.2 }} />
                      <ListItemText primary="cat" primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} />
                      <Typography variant="caption" color="text.secondary">1</Typography>
                    </ListItemButton>
                  </List>
                </Box>

                <Divider />

                <Box sx={{ p: 2, mt: 'auto' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>
                    Unused Classes
                  </Typography>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default', mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">person</Typography>
                  </Box>
                  <Button fullWidth variant="outlined" startIcon={<AutoFixHighRoundedIcon />} sx={{ textTransform: 'none', borderRadius: 2 }}>
                    Find Objects with AI
                  </Button>
                </Box>
              </LeftEditorRail>

              <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(to right, transparent 49.8%, rgba(255,255,255,0.9) 50%, transparent 50.2%), linear-gradient(to bottom, transparent 49.8%, rgba(255,255,255,0.9) 50%, transparent 50.2%)', pointerEvents: 'none', opacity: 0.75 }} />
                <Box
                  sx={{
                    position: 'relative',
                    maxWidth: '82%',
                    maxHeight: '86%',
                    transform: `scale(${zoom / 100})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.12s ease-out',
                    boxShadow: 8,
                  }}
                >
                  <Box
                    component="img"
                    src="/static/demo4.png"
                    alt="annotation editor"
                    sx={{
                      display: 'block',
                      width: '100%',
                      maxWidth: 1100,
                      maxHeight: '70vh',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <Box
                  onMouseDown={(event) => startRectInteraction(event, 'move')}
                  sx={{
                    position: 'absolute',
                    top: `${annotationRect.y}%`,
                    left: `${annotationRect.x}%`,
                    width: `${annotationRect.width}px`,
                    height: `${annotationRect.height}px`,
                    border: '4px solid #00ff14',
                    cursor: activeTool === 'move' ? 'grab' : 'move',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      right: -8,
                      bottom: -8,
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: '#00ff14',
                      border: '2px solid #fff',
                    },
                  }}
                >
                  <Box
                    onMouseDown={(event) => startRectInteraction(event, 'resize')}
                    sx={{
                      position: 'absolute',
                      right: -10,
                      bottom: -10,
                      width: 18,
                      height: 18,
                      cursor: 'nwse-resize',
                    }}
                  />
                </Box>
                <Box sx={{ position: 'absolute', top: `calc(${annotationRect.y}% - 6%)`, left: `${annotationRect.x}%`, bgcolor: '#00ff14', color: '#fff', px: 1.2, py: 0.4, fontWeight: 700, fontSize: 16 }}>
                  person 0.91
                </Box>

                <Stack direction="row" spacing={1} sx={{ position: 'absolute', left: 14, bottom: 12 }}>
                  <Button size="small" variant="outlined" onClick={() => updateZoom(-10)} sx={{ minWidth: 34, borderRadius: 2 }}>-</Button>
                  <Box sx={{ px: 1.5, py: 0.7, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="caption">{zoom}%</Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => updateZoom(10)} sx={{ minWidth: 34, borderRadius: 2 }}>+</Button>
                  <Button size="small" onClick={() => setZoom(100)} sx={{ borderRadius: 2 }}>Reset</Button>
                </Stack>
              </Box>

              <ToolRail>
                {[
                  { key: 'pan', icon: <PanToolAltRoundedIcon fontSize="small" /> },
                  { key: 'box', icon: <CropFreeRoundedIcon fontSize="small" /> },
                  { key: 'move', icon: <OpenWithRoundedIcon fontSize="small" /> },
                  { key: 'focus', icon: <CenterFocusStrongRoundedIcon fontSize="small" /> },
                  { key: 'settings', icon: <TuneRoundedIcon fontSize="small" /> },
                  { key: 'tag', icon: <SellRoundedIcon fontSize="small" /> },
                  { key: 'zoom', icon: <ZoomInRoundedIcon fontSize="small" /> },
                  { key: 'undo', icon: <UndoRoundedIcon fontSize="small" /> },
                  { key: 'redo', icon: <RedoRoundedIcon fontSize="small" /> },
                  { key: 'clear', icon: <CloseRoundedIcon fontSize="small" /> },
                ].map((tool) => (
                  <IconButton
                    key={tool.key}
                    size="small"
                    onClick={() => setActiveTool(tool.key)}
                    sx={{
                      border: '1px solid',
                      borderColor: activeTool === tool.key ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      color: activeTool === tool.key ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {tool.icon}
                  </IconButton>
                ))}
              </ToolRail>
            </Box>
          </Box>
        </Dialog>
      </>
    ),
    side: null,
  };
}
